import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { useDocsPage } from '../hooks/useDocsPage';

const BASE = 'https://api.revvly.ai/api/v1/forms';

const FORM_RESPONSE = `{
  "publicId": "Xk2mQ8vT1nR4pLw7Za",
  "name": "Book a demo",
  "description": null,
  "version": 3,
  "settings": {
    "submitLabel": "Request a demo",
    "successMessage": "Thanks, we will be in touch within a day.",
    "redirectUrl": null
  },
  "fields": [
    {
      "id": "fld_1", "key": "email", "type": "EMAIL", "label": "Work email",
      "description": null, "placeholder": "you@company.com", "required": true,
      "options": [], "maxLength": null, "urlParam": null,
      "maxFiles": null, "maxFileSizeMb": null, "accept": null
    },
    {
      "id": "fld_2", "key": "team_size", "type": "DROPDOWN", "label": "Team size",
      "description": null, "placeholder": null, "required": false,
      "options": [{ "id": "o1", "label": "1-10" }, { "id": "o2", "label": "11-50" }],
      "maxLength": null, "urlParam": null,
      "maxFiles": null, "maxFileSizeMb": null, "accept": null
    },
    {
      "id": "fld_3", "key": "utm_source", "type": "HIDDEN", "label": "Source",
      "description": null, "placeholder": null, "required": false,
      "options": [], "maxLength": null, "urlParam": "utm_source",
      "maxFiles": null, "maxFileSizeMb": null, "accept": null
    }
  ]
}`;

const SUBMIT_REQUEST = `POST ${BASE}/Xk2mQ8vT1nR4pLw7Za/submissions
Content-Type: application/json

{
  "answers": {
    "email": "ada@example.com",
    "team_size": "11-50",
    "utm_source": "newsletter"
  },
  "website": "",
  "pageUrl": "https://example.com/demo?utm_source=newsletter",
  "referrer": "https://www.google.com/"
}`;

const SUBMIT_RESPONSE = `201 Created
{
  "submissionId": "6705f2c1a4b3e2d1c0f9e8d7",
  "successMessage": "Thanks, we will be in touch within a day.",
  "redirectUrl": null
}`;

const SUBMIT_ERROR = `400 Bad Request
{
  "message": "Some answers need attention.",
  "fieldErrors": {
    "email": "Enter a valid email address.",
    "team_size": "Pick one of the listed options."
  }
}`;

const UPLOAD_REQUEST = `POST ${BASE}/Xk2mQ8vT1nR4pLw7Za/uploads
Content-Type: application/json

{ "fieldKey": "cv", "filename": "ada-lovelace.pdf", "size": 482113 }`;

const UPLOAD_RESPONSE = `201 Created
{
  "fileId": "6705f2c1a4b3e2d1c0f9e8a1",
  "uploadUrl": "https://…signed upload address…",
  "contentType": "application/pdf",
  "maxBytes": 31457280,
  "expiresAt": "2026-10-09T10:15:00.000Z"
}

// Then, from your server:
PUT <uploadUrl>
Content-Type: application/pdf      <- exactly the contentType returned
<the file's bytes>

// And submit the id as the answer:
"answers": { "cv": ["6705f2c1a4b3e2d1c0f9e8a1"] }`;

const SUBMIT_FORM = `mutation Submit($input: SubmitFormInput!) {
  submitForm(input: $input) {
    submissionId
    duplicate
    successMessage
    redirectUrl
  }
}`;

const SUBMIT_FORM_VARIABLES = `{
  "input": {
    "publicId": "Xk2mQ8vT1nR4pLw7Za",
    "answers": { "email": "ada@example.com", "team_size": "11-50" },
    "idempotencyKey": "contact-form-7f3a9c",
    "pageUrl": "https://example.com/demo"
  }
}`;

const SUBMIT_FORM_NODE = `// server.js. The key lives here and nowhere else.
const res = await fetch('https://api.revvly.ai/api/v1/graphql', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.REVVLY_FORMS_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ query: SUBMIT_FORM, variables }),
});
const { data, errors } = await res.json();
const error = errors?.[0];
if (error?.extensions?.code === 'BAD_REQUEST') {
  const fieldErrors = error.extensions.fieldErrors;
  // Invalid answers carry { "<field key>": "<message>" }: show each under its question.
  // A BAD_REQUEST without it is a problem with the call itself (both formId and
  // publicId sent, an idempotencyKey over 128 characters): the message says which.
  return fieldErrors ? showErrors(fieldErrors) : showProblem(error.message);
}`;

const ANSWER_FORMATS: [string, string][] = [
  ['SHORT_TEXT, LONG_TEXT', 'A string, within maxLength when set.'],
  ['RICH_TEXT', 'A string of Markdown or simple HTML. Scripts and styles are removed.'],
  ['EMAIL', 'A valid email address.'],
  [
    'PHONE',
    '5 to 40 characters: digits, spaces, dots, slashes, hyphens and parentheses, with an optional + at the start only.',
  ],
  ['URL', 'A web address. https:// is added when missing.'],
  ['NUMBER', 'A number, or a string holding one.'],
  ['DATE', 'A string, YYYY-MM-DD.'],
  ['SINGLE_CHOICE, DROPDOWN', 'One option label, exactly as listed in options.'],
  ['MULTIPLE_CHOICE', 'An array of option labels.'],
  ['CONSENT', 'true when ticked. Leave it out when not.'],
  ['HIDDEN', 'A string, usually read from the page URL parameter named in urlParam.'],
  ['FILE_UPLOAD', 'An array of fileIds from the uploads endpoint, at most maxFiles.'],
  ['HEADING, PARAGRAPH', 'Layout only. Send nothing.'],
];

export function FormsApi() {
  useDocsPage('Forms API — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Forms</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API reference</span>
        <h1>
          <em>Forms</em> API.
        </h1>
        <p className="dp-oneliner">
          Build your own front end for a Revvly form. Read the published form, upload files and
          submit answers from the browser with no key, or submit from your server with an API key.
          Every submission runs the form&rsquo;s steps exactly as one from the hosted page would.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="overview">Which to use</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">From the browser</div>
            <div className="dp-field-v">
              The three REST routes below. No key; they accept requests from any website, without
              cookies. Each is addressed by the form&rsquo;s public id, the one in its hosted link.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">From your server</div>
            <div className="dp-field-v">
              The <span className="api-inline">submitForm</span> GraphQL mutation, with an API key
              that has the &ldquo;Can submit forms&rdquo; permission. Adds retry-safe idempotency
              keys and a higher daily limit.
            </div>
          </div>
        </div>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            The form&rsquo;s Share tab has a prompt for a coding agent that already contains your
            form&rsquo;s fields, answer formats and these endpoints. Start there; this page is the
            reference behind it. Setup and sharing are covered in the{' '}
            <Link to="/docs/guides/forms">Forms guide</Link>.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="get-form">Read a published form</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-get">GET</span>
          <span className="api-url">{BASE}/:publicId</span>
          <span className="api-returns">200 OK</span>
        </div>
        <p className="dp-section-sub">
          The live version of the form: its questions in order, and the settings for the button,
          thank-you message and redirect. Edits in Revvly appear here only once they are published,
          so build from this, not from the builder. A form that is not published, archived or
          unknown is a 404.
        </p>
        <pre className="api-code">{FORM_RESPONSE}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="answers">Answer formats</AnchorHeading>
        <p className="dp-section-sub">
          <span className="api-inline">answers</span> is an object keyed by each field&rsquo;s{' '}
          <span className="api-inline">key</span>. Leave out questions the person did not answer.
          Unknown keys are ignored.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          {ANSWER_FORMATS.map(([k, v]) => (
            <div className="dp-field-row" key={k}>
              <div className="dp-field-k">
                <span className="api-inline">{k}</span>
              </div>
              <div className="dp-field-v">{v}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="post-submission">Submit answers</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">{BASE}/:publicId/submissions</span>
          <span className="api-returns">201 Created</span>
        </div>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">answers</span>
            </div>
            <div className="dp-field-v">
              Required. See <a href="#answers">Answer formats</a>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">source</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Leave it out. Submissions to this route are recorded as coming from{' '}
              <strong>Custom</strong>, a form you built into your own site, so you can tell them
              from the share link and the embed in Submissions and in the CSV. (Revvly&rsquo;s own
              pages send <span className="api-inline">HOSTED</span> or{' '}
              <span className="api-inline">EMBED</span>; submissions from your server through{' '}
              <span className="api-inline">submitForm</span> are <strong>API</strong>.)
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">website</span>
              <span className="api-badge api-badge-opt">spam trap</span>
            </div>
            <div className="dp-field-v">
              Render a text input named <span className="api-inline">website</span> that people
              cannot see or reach (off-screen,{' '}
              <span className="api-inline">tabindex=&quot;-1&quot;</span>,{' '}
              <span className="api-inline">aria-hidden=&quot;true&quot;</span>, not{' '}
              <span className="api-inline">display: none</span>) and send its value as it is. A
              filled one gets the normal success response and is silently discarded.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">pageUrl</span>,{' '}
              <span className="api-inline">referrer</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              <span className="api-inline">window.location.href</span> and{' '}
              <span className="api-inline">document.referrer</span>, shown with the submission.
            </div>
          </div>
        </div>
        <span className="api-code-label">Request</span>
        <pre className="api-code">{SUBMIT_REQUEST}</pre>
        <span className="api-code-label">Success</span>
        <pre className="api-code">{SUBMIT_RESPONSE}</pre>
        <p className="dp-section-sub">
          Replace the form with <span className="api-inline">successMessage</span>, or go to{' '}
          <span className="api-inline">redirectUrl</span> when it is set.
        </p>
        <span className="api-code-label">Invalid answers</span>
        <pre className="api-code">{SUBMIT_ERROR}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">400</div>
            <div className="dp-field-v">
              Show each <span className="api-inline">fieldErrors</span> message under its question,
              keep what the person typed, and move focus to the first one.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">404</div>
            <div className="dp-field-v">The form is not published right now.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">429</div>
            <div className="dp-field-v">
              Too many submissions from this visitor (30 a minute) or to this form today (1,000).
              Ask the person to try again later.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="post-upload">Upload a file</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-post">POST</span>
          <span className="api-url">{BASE}/:publicId/uploads</span>
          <span className="api-returns">201 Created</span>
        </div>
        <p className="dp-section-sub">
          For <span className="api-inline">FILE_UPLOAD</span> questions. Ask for an upload address
          per file, upload the bytes to it, then submit the returned{' '}
          <span className="api-inline">fileId</span>s as the answer. The type is decided by the file
          name&rsquo;s extension; a type or size the question does not accept is a 400 here. When
          the form is submitted, each file is checked for its real type and scanned before it is
          kept, and a file that fails comes back as that question&rsquo;s{' '}
          <span className="api-inline">fieldErrors</span> entry. Files never submitted are deleted
          after a day.
        </p>
        <pre className="api-code">{UPLOAD_REQUEST}</pre>
        <pre className="api-code">{UPLOAD_RESPONSE}</pre>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            The visitor&rsquo;s browser can send the file straight to the upload address from your
            site. If the browser reports a cross-origin (CORS) error on that request, do the upload
            step from your server instead: it receives the file, asks for the address and uploads
            it. Or keep file questions on the hosted or embedded form.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="submit-form">submitForm (GraphQL, from your server)</AnchorHeading>
        <div className="api-endpoint-bar">
          <span className="api-method api-method-mutation">MUTATION</span>
          <span className="api-url">https://api.revvly.ai/api/v1/graphql</span>
          <span className="api-returns">FormSubmissionReceipt</span>
        </div>
        <p className="dp-section-sub">
          Submit to one of your workspace&rsquo;s published forms with an API key sent as{' '}
          <span className="api-inline">Authorization: Bearer &lt;key&gt;</span>. The key needs the
          &ldquo;Can submit forms&rdquo; permission (Settings, API keys); a key with only write
          access is refused, and a forms key can do nothing else. Never put this key in browser
          code.
        </p>
        <pre className="api-code">{SUBMIT_FORM}</pre>
        <span className="api-code-label">Variables</span>
        <pre className="api-code">{SUBMIT_FORM_VARIABLES}</pre>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">publicId</span> or{' '}
              <span className="api-inline">formId</span>
            </div>
            <div className="dp-field-v">
              Exactly one. The form is only ever looked up in the key&rsquo;s own workspace.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              <span className="api-inline">idempotencyKey</span>
              <span className="api-badge api-badge-opt">optional</span>
            </div>
            <div className="dp-field-v">
              Up to 128 characters, unique per submission. Retrying with the same key returns the
              first submission with <span className="api-inline">duplicate: true</span> instead of
              storing another, so a timed-out request is safe to repeat.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Errors</div>
            <div className="dp-field-v">
              <span className="api-inline">BAD_REQUEST</span> with{' '}
              <span className="api-inline">extensions.fieldErrors</span> for invalid answers,{' '}
              <span className="api-inline">NOT_FOUND</span> for a form that is not published,{' '}
              <span className="api-inline">FORBIDDEN</span> without the permission or without Forms,
              and <span className="api-inline">RATE_LIMITED</span> past 10,000 a day per form.
            </div>
          </div>
        </div>
        <pre className="api-code">{SUBMIT_FORM_NODE}</pre>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/forms">Collect answers with Forms ›</Link>
          </li>
          <li>
            <Link to="/docs/api/graphql-public">Public GraphQL API ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/api-keys">Configure integration API keys ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
